import ApplicationController from "application_controller.js";

//* Connectivity controller — shows/hides Rails-rendered notifications
//* on offline/online transitions and disables network-dependent buttons.
//*
//* Works with two template modes:
//*   - Bootstrap: uses Bootstrap Toast JS to show/hide .toast elements
//*   - Tailwind: simply toggles the `hidden` attribute on plain divs
//*
//* The toast HTML is pre-rendered by the server (shared/_connectivity_notice.html.erb).
//*
//* Targets:
//*   - offlineToast: pre-rendered offline notification element
//*   - reconnectedToast: pre-rendered reconnected notification element
//*   - toastContainer: the fixed-position wrapper
//*   - networkButton: button(s) to disable when offline

export default class extends ApplicationController {

  debug = false;

  static ALLOWED_TEMPLATES = ["bootstrap", "tailwind"];

  static targets = ["offlineToast", "reconnectedToast", "toastContainer", "networkButton"];

  get template() {
    if (!this.hasToastContainerTarget) return null;
    return this.toastContainerTarget.dataset.connectivityTemplate || null;
  }

  get isBootstrap() { return this.template === "bootstrap"; }
  get isTailwind()  { return this.template === "tailwind";  }

  connect() {
    this.log_function("connect");

    if (this.template && !this.constructor.ALLOWED_TEMPLATES.includes(this.template)) {
      console.error(
        `[connectivity] Unknown template: "${this.template}". Must be one of: ${this.constructor.ALLOWED_TEMPLATES.join(", ")}.`,
      );
    }

    this.offlineHandler = this.handleOffline.bind(this);
    this.onlineHandler = this.handleOnline.bind(this);
    this.childOfflineHandler = this.handleOffline.bind(this);

    window.addEventListener("offline", this.offlineHandler);
    window.addEventListener("online", this.onlineHandler);

    //* Listen for dispatched offline events from child controllers:
    //* (infinite-scroll, events-channel, filter, etc.)
    this.element.addEventListener("infinite-scroll:offline", this.childOfflineHandler);
    this.element.addEventListener("events-channel:offline", this.childOfflineHandler);
    this.element.addEventListener("filter:offline", this.childOfflineHandler);

    //* If already offline on page load, show immediately
    if (!navigator.onLine) {
      this.handleOffline();
    }
  }

  disconnect() {
    this.log_function("disconnect");

    window.removeEventListener("offline", this.offlineHandler);
    window.removeEventListener("online", this.onlineHandler);
    this.element.removeEventListener("infinite-scroll:offline", this.childOfflineHandler);
    this.element.removeEventListener("events-channel:offline", this.childOfflineHandler);
    this.element.removeEventListener("filter:offline", this.childOfflineHandler);
  }

  handleOffline() {
    this.log_function("handleOffline");

    if (this.isOffline) return;
    this.isOffline = true;

    this.showOfflineToast();
    this.disableNetworkButtons();
  }

  handleOnline() {
    this.log_function("handleOnline");

    this.isOffline = false;
    this.hideOfflineToast();
    this.showReconnectedToast();
    this.enableNetworkButtons();
  }

  //* ─── Toast management ──────────────────────────────────────────────────────

  showOfflineToast() {
    this.log_function("showOfflineToast");

    if (!this.hasOfflineToastTarget) return;

    this.showElement(this.offlineToastTarget);

    //* Auto-hide for Tailwind templates
    if (this.isTailwind) {
      this.offlineToastTimer = setTimeout(() => this.hideElement(this.offlineToastTarget), 5000);
    }
  }

  hideOfflineToast() {
    this.log_function("hideOfflineToast");

    if (!this.hasOfflineToastTarget) return;
    clearTimeout(this.offlineToastTimer);
    this.hideElement(this.offlineToastTarget);
  }

  showReconnectedToast() {
    this.log_function("showReconnectedToast");

    if (!this.hasReconnectedToastTarget) return;

    this.showElement(this.reconnectedToastTarget);

    //* Auto-hide for Tailwind templates
    if (this.isTailwind) {
      setTimeout(() => this.hideElement(this.reconnectedToastTarget), 3000);
    }
  }

  //* Manual dismiss for Tailwind template close button
  dismissOffline() {
    this.log_function("dismissOffline");

    this.hideOfflineToast();
  }

  //* Manual dismiss for Tailwind template close button
  dismissReconnected() {
    this.log_function("dismissReconnected");

    this.hideReconnectedToast();
  }

  //* ─── Element show/hide (Bootstrap Toast or plain toggle) ───────────────────

  showElement(el) {
    this.log_function("showElement");

    el.hidden = false;

    if (this.isBootstrap) {
      this.withToastClass((ToastClass) => {
        ToastClass.getOrCreateInstance(el).show();
      });
    }
  }

  hideElement(el) {
    this.log_function("hideElement");

    if (this.isBootstrap) {
      this.withToastClass((ToastClass) => {
        ToastClass.getOrCreateInstance(el).hide();
      });
    } else {
      el.hidden = true;
    }
  }

  async withToastClass(callback) {
    this.log_function("withToastClass");

    if (this._ToastClass) {
      callback(this._ToastClass);
      return;
    }

    try {
      const mod = await import("bootstrap/js/dist/toast");
      this._ToastClass = mod.default;
      callback(this._ToastClass);
    } catch {
      //* Bootstrap Toast not available — elements remain visible via hidden attr
    }
  }

  //* ─── Network-dependent button management ───────────────────────────────────

  disableNetworkButtons() {
    this.log_function("disableNetworkButtons");

    this.networkButtonTargets.forEach((btn) => {
      btn.disabled = true;
      btn.dataset.connectivityDisabled = "true";
    });
  }

  enableNetworkButtons() {
    this.log_function("enableNetworkButtons");

    this.networkButtonTargets.forEach((btn) => {
      if (btn.dataset.connectivityDisabled === "true") {
        btn.disabled = false;
        delete btn.dataset.connectivityDisabled;
      }
    });
  }
}
